import { ExcelController } from "@controllers/Excel.controller";
import {
  type Association,
  OpportunityParticipant,
} from "@entities";
import PersonAddRoundedIcon from "@mui/icons-material/PersonAddRounded";
import CancelIcon from "@mui/icons-material/Cancel";
import FileUploadIcon from "@mui/icons-material/FileUpload";
import { CircularProgress, Stack } from "@mui/material";
import { useCreateHyperTable } from "@shared/frontend/hyper-table";
import { type ActionButton } from "@shared/frontend/hyper-table/inner-components/hyper-table-actions-bar/ActionsBar";
import { isEntityFilter } from "@shared/frontend/hyper-table/inner-components/hyper-table-toolbar/FilterMenuItem";
import { useQueryClient } from "@tanstack/react-query";
import { type FC, useMemo, useState } from "react";
import { UserCardPopup } from "../../../components/popups/user-card/UserCardPopup";
import { useOrganization } from "../../../contexts/OrganizationProvider";
import { invalidateEventParticipantQueries } from "../../../queries/invalidate-opportunities-table";
import { opportunityParticipantRepo } from "@repositories";
import { linko } from "@shared/linko";
import {
  ADMIN_BULK_ACTION_LIMIT,
  adminParticipantActionErrorMessage,
  runInChunks,
} from "../../../helpers/admin-list-query";
import { useOpportunityInterestedColumns } from "./columns";
import filters from "./filters";
import { useAlert } from "../../../contexts/AlertProvider";
import { downloadExcelFile } from "../../../helpers/excel/export";

const OpportunityRequestsTable: FC<{
  id: string;
  refetchEvent?: () => void;
  onApprovedGroup?: () => void;
}> = ({ id, refetchEvent, onApprovedGroup }) => {
  const { HyperTable } = useCreateHyperTable(OpportunityParticipant);
  const { currOrganization } = useOrganization();
  const queryClient = useQueryClient();
  const [currentUserRowData, setCurrentUserRowData] = useState<Association>();
  const { setAlertText, setSeverity } = useAlert();

  const refreshAfterChange = async () => {
    await invalidateEventParticipantQueries(queryClient, id);
    refetchEvent?.();
    HyperTable.context.current?.refetch("remove");
  };

  const columns = useOpportunityInterestedColumns(HyperTable, (approvedGroup) => {
    refetchEvent?.();
    if (approvedGroup) onApprovedGroup?.();
  });

  const buttons: ActionButton<OpportunityParticipant>[] = useMemo(() => {
    return [
      {
        icon: <PersonAddRoundedIcon />,
        onClick: async ({ whereToUpdate }) => {
          if (!whereToUpdate) return;

          const participants = await opportunityParticipantRepo.find({
            where: {
              $and: [whereToUpdate],
            },
            limit: ADMIN_BULK_ACTION_LIMIT + 1,
          });

          if (participants.length === 0) {
            setAlertText("לא נמצאו בקשות לאישור");
            setSeverity("warning");
            return;
          }

          const toProcess = participants.slice(0, ADMIN_BULK_ACTION_LIMIT);
          try {
            await runInChunks(toProcess, 4, async (volunteer) => {
              await linko.opportunityParticipant.mutations.approveOrDeclineInterested(
                {
                  opportunityId: volunteer.opportunityId,
                  associationId: volunteer.associationId,
                  isApproved: true,
                },
              );
            });
          } catch (error) {
            setAlertText(adminParticipantActionErrorMessage(error));
            setSeverity("error");
            return;
          }

          const count = toProcess.length;
          const groupCount = toProcess.filter((p) => p.groupId).length;
          const truncated = participants.length > ADMIN_BULK_ACTION_LIMIT;
          setAlertText(
            `${
              groupCount > 0 && groupCount === count
                ? count === 1
                  ? "הקבוצה אושרה והוקצתה למשימה בהצלחה"
                  : `${count} קבוצות אושרו והוקצו למשימה בהצלחה`
                : count === 1
                  ? "מתנדב נוסף למשימה בהצלחה"
                  : `${count} מתנדבים נוספו למשימה בהצלחה`
            }${truncated ? `. אושרו עד ${ADMIN_BULK_ACTION_LIMIT} בכל פעולה` : ""}`,
          );
          setSeverity("success");

          await refreshAfterChange();
          if (groupCount > 0) onApprovedGroup?.();
        },
        title: "אישור בקשות (מתנדבים / קבוצות)",
      },
      {
        icon: <CancelIcon />,
        onClick: async ({ whereToUpdate }) => {
          if (!whereToUpdate) return;

          const participants = await opportunityParticipantRepo.find({
            where: {
              $and: [whereToUpdate],
            },
            limit: ADMIN_BULK_ACTION_LIMIT + 1,
          });

          if (participants.length === 0) {
            setAlertText("לא נמצאו בקשות לדחייה");
            setSeverity("warning");
            return;
          }

          const toProcess = participants.slice(0, ADMIN_BULK_ACTION_LIMIT);
          try {
            await runInChunks(toProcess, 4, async (volunteer) => {
              await linko.opportunityParticipant.mutations.approveOrDeclineInterested(
                {
                  opportunityId: volunteer.opportunityId,
                  associationId: volunteer.associationId,
                  isApproved: false,
                },
              );
            });
          } catch (error) {
            setAlertText(adminParticipantActionErrorMessage(error));
            setSeverity("error");
            return;
          }

          const count = toProcess.length;
          const groupCount = toProcess.filter((p) => p.groupId).length;
          const truncated = participants.length > ADMIN_BULK_ACTION_LIMIT;
          setAlertText(
            `${
              groupCount > 0 && groupCount === count
                ? count === 1
                  ? "בקשת הקבוצה נדחתה בהצלחה"
                  : `${count} בקשות קבוצה נדחו בהצלחה`
                : count === 1
                  ? "מתנדב נדחה בהצלחה"
                  : `${count} מתנדבים נדחו בהצלחה`
            }${truncated ? `. נדחו עד ${ADMIN_BULK_ACTION_LIMIT} בכל פעולה` : ""}`,
          );
          setSeverity("success");
          await refreshAfterChange();
        },
        title: "דחיית בקשות",
      },
      {
        icon: <FileUploadIcon />,
        onClick: async ({ whereToUpdate }) => {
          if (!whereToUpdate) return;

          const excelResult =
            await ExcelController.exportInterestedInOpportunitiesToExcel(
              whereToUpdate,
            );
          if (excelResult) {
            downloadExcelFile(excelResult);
            return {
              refetch: false,
              resetSelection: true,
            };
          }
          setAlertText("לא נמצאו מתעניינים לייצוא לפי הבחירה");
          setSeverity("warning");
          return {
            refetch: false,
            resetSelection: true,
          };
        },
        title: "ייצוא לאקסל (מתעניינים)",
      },
    ];
  }, [id, setAlertText, setSeverity, queryClient, HyperTable, refetchEvent]);

  if (!currOrganization)
    return (
      <Stack width="100%" height="100%" paddingTop="10vh" alignItems="center">
        <CircularProgress />
      </Stack>
    );

  return (
    <HyperTable
      tableId="opportunity-interested-table"
      findConfig={{
        where: {
          opportunity: { $id: id },
          status: "PENDING",
        },
        include: {
          group: true,
          association: {
            include: {
              user: true,
            },
          },
        },
      }}
      onRowClick={(_, row) => setCurrentUserRowData(row.original?.association)}
      title={null}
    >
      <HyperTable.Toolbar>
        {filters.map((filter) =>
          isEntityFilter(filter) ? (
            <HyperTable.EntityFilter key={filter.id} {...(filter as any)} />
          ) : (
            <HyperTable.CustomFilter key={filter.id} {...(filter as any)} />
          ),
        )}
      </HyperTable.Toolbar>
      {columns.map((column) => (
        <HyperTable.Column key={column.field} {...column} />
      ))}
      <HyperTable.SelectColumn />
      <HyperTable.ActionsBar buttons={buttons} />
      <UserCardPopup
        handleSave={async () => {
          setCurrentUserRowData(undefined);
          HyperTable.context?.current?.refetch("remove");
        }}
        userData={currentUserRowData}
        setUserData={setCurrentUserRowData}
      />
    </HyperTable>
  );
};

export default OpportunityRequestsTable;
